import { ChangeEvent, FormEvent, useMemo, useState } from 'react';

type FormStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

type FormValues = {
  company: string;
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  companyAlt: string;
};

const SERVICE_OPTIONS = [
  { value: '', label: '選択してください' },
  { value: 'service', label: 'サービスに関するご相談' },
  { value: 'support', label: '業務サポートのご相談' },
  { value: 'estimate', label: 'お見積もり依頼' },
  { value: 'other', label: 'その他' },
];

const INITIAL_FORM_VALUES: FormValues = {
  company: '',
  name: '',
  email: '',
  phone: '',
  service: '',
  message: '',
  companyAlt: '',
};

export default function Contact() {
  const [formValues, setFormValues] = useState<FormValues>(INITIAL_FORM_VALUES);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');

  const serviceLabel = useMemo(() => {
    return SERVICE_OPTIONS.find((option) => option.value === formValues.service)?.label || '未選択';
  }, [formValues.service]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormValues(INITIAL_FORM_VALUES);
    setPrivacyAccepted(false);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formValues.companyAlt.trim() !== '') {
      setFormStatus('success');
      return;
    }

    if (!privacyAccepted) {
      setFormError('プライバシーポリシーに同意してください。');
      setFormStatus('error');
      return;
    }

    setFormError('');
    setFormStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams({
        company: formValues.company,
        name: formValues.name,
        email: formValues.email,
        phone: formValues.phone,
        service: serviceLabel === '選択してください' ? '' : serviceLabel,
        message: formValues.message,
        privacy: privacyAccepted ? '同意済み' : '',
      });

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: params.toString(),
      });

      const responseText = (await response.text()).trim();

      if (response.ok && responseText === 'OK') {
        setFormStatus('success');
        resetForm();
      } else {
        setFormError('送信に失敗しました。お急ぎの場合は別の方法でお問い合わせください。');
        setFormStatus('error');
      }
    } catch {
      setFormError('送信に失敗しました。お急ぎの場合は別の方法でお問い合わせください。');
      setFormStatus('error');
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-cyan-50/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Contact Us
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            お問い合わせ
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            ご相談内容に応じて、順次ご案内いたします。
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <form id="luce-contact-form" onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                  会社名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formValues.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all text-sm"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                  ご担当者名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formValues.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all text-sm"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all text-sm"
                  placeholder="example@company.co.jp"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all text-sm"
                  placeholder="03-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-slate-700 mb-2">
                  ご相談種別
                </label>
                <select
                  id="service"
                  name="service"
                  value={formValues.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-8 border border-slate-300 rounded-md focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all text-sm cursor-pointer"
                >
                  {SERVICE_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formValues.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all resize-none text-sm"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs text-slate-500 mt-1">{formValues.message.length}/500文字</p>
              </div>

              <input
                type="text"
                name="companyAlt"
                value={formValues.companyAlt}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="sr-only"
              />

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 text-cyan-600 border-slate-300 rounded focus:ring-cyan-500 cursor-pointer"
                />
                <label htmlFor="privacy" className="text-sm text-slate-700 cursor-pointer">
                  <a
                    href="/privacy"
                    className="text-cyan-600 hover:text-cyan-700 underline"
                  >
                    プライバシーポリシー
                  </a>
                  に同意します <span className="text-red-500">*</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-cyan-600 text-white py-4 rounded-md font-semibold hover:bg-cyan-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '送信中...' : '送信する'}
              </button>
            </form>
          </div>

          <div className="space-y-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">お問い合わせ先</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-cyan-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 mb-1">所在地</h4>
                    <p className="text-slate-600">東京都世田谷区松原２丁目２１番１０号ＢＥＳ明大前コート１０２</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-lg flex-shrink-0">
                    <i className="ri-building-line text-xl text-cyan-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 mb-1">会社名</h4>
                    <p className="text-slate-600">合同会社ｌｕｃｅ</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-lg flex-shrink-0">
                    <i className="ri-chat-1-line text-xl text-cyan-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 mb-1">ご案内</h4>
                    <p className="text-slate-600">ご相談内容を確認のうえ、順次折り返しご連絡いたします。</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">アクセス</h3>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
                <p className="text-slate-700 leading-relaxed">
                  東京都世田谷区松原２丁目２１番１０号
                  <br />
                  ＢＥＳ明大前コート１０２
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {formStatus === 'confirm' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 md:px-8 py-6 border-b border-slate-200">
              <p className="text-xs tracking-[0.2em] text-cyan-600 mb-2">CONFIRM</p>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900">入力内容の確認</h3>
              <p className="text-sm text-slate-500 mt-2">送信前に内容をご確認ください。</p>
            </div>

            <div className="px-6 md:px-8 py-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-slate-400 mb-1">会社名</p>
                  <p className="text-sm text-slate-800 break-words">{formValues.company}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-slate-400 mb-1">ご担当者名</p>
                  <p className="text-sm text-slate-800 break-words">{formValues.name}</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-slate-400 mb-1">メールアドレス</p>
                  <p className="text-sm text-slate-800 break-all">{formValues.email}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-slate-400 mb-1">電話番号</p>
                  <p className="text-sm text-slate-800 break-words">{formValues.phone || '未入力'}</p>
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-400 mb-1">ご相談種別</p>
                <p className="text-sm text-slate-800 break-words">{serviceLabel === '選択してください' ? '未選択' : serviceLabel}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-400 mb-1">お問い合わせ内容</p>
                <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{formValues.message}</p>
              </div>
            </div>

            <div className="px-6 md:px-8 py-5 border-t border-slate-200 flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setFormStatus('idle')}
                className="px-5 py-3 rounded-full border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-100 transition-colors"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {formStatus === 'submitting' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-5">
              <i className="ri-loader-4-line animate-spin text-2xl text-cyan-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">送信中</h3>
            <p className="text-sm text-slate-500">お問い合わせ内容を送信しています。</p>
          </div>
        </div>
      )}

      {formStatus === 'success' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-cyan-50 flex items-center justify-center mb-5">
              <i className="ri-checkbox-circle-line text-2xl text-cyan-700"></i>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">送信完了</h3>
            <p className="text-sm text-slate-500 mb-6">
              お問い合わせを受け付けました。内容を確認のうえ順次ご連絡いたします。
            </p>
            <button
              type="button"
              onClick={() => setFormStatus('idle')}
              className="px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {formStatus === 'error' && formError && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-5">
              <i className="ri-error-warning-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">送信失敗</h3>
            <p className="text-sm text-slate-500 mb-6">{formError}</p>
            <button
              type="button"
              onClick={() => setFormStatus('idle')}
              className="px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
